import React, { useState, useEffect } from "react";
import api from "../../../api/api";
import AdminPanel from "../panel/AdminPanel";
import "../styles/ClassTimetable.css";

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const defaultSlots = [
  { time: "9:00 - 10:00", subject_name: "", teacher_id: "", room: "", message: "" },
  { time: "10:00 - 11:00", subject_name: "", teacher_id: "", room: "", message: "" },
  { time: "11:00 - 12:00", subject_name: "", teacher_id: "", room: "", message: "" },
  { time: "12:00 - 1:00", subject_name: "", teacher_id: "", room: "", message: "" },
  { time: "1:00 - 2:00", subject_name: "Lunch Break", teacher_id: "", room: "", message: "", isBreak: true },
  { time: "2:00 - 3:00", subject_name: "", teacher_id: "", room: "", message: "" },
  { time: "3:00 - 4:00", subject_name: "", teacher_id: "", room: "", message: "" },
  { time: "4:00 - 5:00", subject_name: "", teacher_id: "", room: "", message: "" },
];

const ClassTimetable = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [day, setDay] = useState("Monday");
  const [timetable, setTimetable] = useState({});
  const [subjectsList, setSubjectsList] = useState([]);
  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  const token = localStorage.getItem("token");

  // Fetch subjects and classes initially
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subRes, teacherRes, classRes] = await Promise.all([
  api.get("/api/subjects", { headers: { Authorization: `Bearer ${token}` } }),
  api.get("/api/teacher-class", { headers: { Authorization: `Bearer ${token}` } }),
  api.get("/api/classes", { headers: { Authorization: `Bearer ${token}` } }),
]);
        setSubjectsList(subRes.data);
        setTeacherAssignments(teacherRes.data);
        setClasses(classRes.data);
      } catch (err) {
        console.error("Failed to fetch initial data:", err);
      }
    };
    fetchData();
  }, []);

  // Fetch sections whenever a class is selected
  useEffect(() => {
    if (!classId) return setSections([]);

    const fetchSections = async () => {
      try {
        const res = await api.get("/api/sections", { params: { classId }, headers: { Authorization: `Bearer ${token}` } });
        setSections(res.data);
      } catch (err) {
        console.error("Failed to fetch sections:", err);
      }
    };

    fetchSections();
    setSectionId(""); // reset section when class changes
  }, [classId]);

  // Fetch timetable when class + section are selected
  useEffect(() => {
    if (!classId || !sectionId) return;

    const fetchTimetable = async () => {
      try {
        const res = await api.get("/api/timetable/class", {
          params: { classId, sectionId },
          headers: { Authorization: `Bearer ${token}` }
        });

        // Fill empty days with default slots
        const data = { ...res.data };
        days.forEach(d => {
          if (!data[d] || data[d].length === 0) data[d] = [...defaultSlots];
        });

        setTimetable(data);
      } catch (err) {
        console.error("Failed to fetch timetable:", err);
      }
    };

    fetchTimetable();
  }, [classId, sectionId]);

  const handleChange = (day, index, field, value) => {
    const updatedDay = [...(timetable[day] || [])];
    updatedDay[index] = { ...updatedDay[index], [field]: value };
    setTimetable({ ...timetable, [day]: updatedDay });
  };

  const handleSave = async () => {
    if (!classId || !sectionId) return alert("Select class and section first!");

    try {
      for (let d of Object.keys(timetable)) {
        for (let slot of timetable[d]) {
          if (!slot.subject_name || !slot.teacher_id || !slot.time || slot.isBreak) continue;
          await api.post(
            "/api/timetable/save",
            {
              classId,
              sectionId,
              day: d,
              time: slot.time,
              subjectName: slot.subject_name,
              teacherId: slot.teacher_id,
              room: slot.room,
              message: slot.message,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }
      alert("✅ Timetable saved successfully!");
    } catch (err) {
      console.error("Failed to save timetable:", err);
      alert("❌ Error saving timetable");
    }
  };

  return (
    <>
      <AdminPanel onToggle={setIsSidebarCollapsed} />

      <div className={`timetable-full ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <h1 className="timetable-header">📅 Manage Class Timetable</h1>

        <div className="timetable-card">
          {/* Class & Section Selector */}
          <div className="day-selector">
            <label>Class: </label>
            <select value={classId} onChange={(e) => setClassId(e.target.value)}>
              <option value="">-- Select Class --</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <label> Section: </label>
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
              <option value="">-- Select Section --</option>
              {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label> Day: </label>
            <select value={day} onChange={(e) => setDay(e.target.value)}>
              {days.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Timetable Table */}
          <table className="timetable-editor">
            <thead>
              <tr>
                <th>Time</th>
                <th>Subject</th>
                <th>Teacher</th>
                <th>Room</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
  {(timetable[day] || []).map((slot, i) => (
    <tr key={`${slot.time}-${slot.subject_name}-${slot.teacher_id || i}`} className={slot.isBreak ? "break-row" : ""}>
      <td>{slot.time}</td>
      <td>
        {slot.isBreak ? (
          <strong>🍴 {slot.subject_name}</strong>
        ) : (
          <select
            value={slot.subject_name || ""}
            onChange={(e) => handleChange(day, i, "subject_name", e.target.value)}
          >
            <option value="">-- Select Subject --</option>
            {subjectsList.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        )}
      </td>
      <td>
  {!slot.isBreak && (
    <select
      value={slot.teacher_id || ""}
      onChange={(e) => handleChange(day, i, "teacher_id", e.target.value)}
    >
      <option value="">-- Select Teacher --</option>
      {Array.from(
        new Map(
          teacherAssignments
            .filter((t) => t.subject_name === slot.subject_name)
            .map((t) => [t.teacher_id, t]) // key = teacher_id
        ).values()
      ).map((t) => (
        <option key={t.teacher_id} value={t.teacher_id}>
          {t.teacher_name}
        </option>
      ))}
    </select>
  )}
</td>

      <td>
        {!slot.isBreak && (
          <input
            type="text"
            value={slot.room || ""}
            onChange={(e) => handleChange(day, i, "room", e.target.value)}
          />
        )}
      </td>
      <td>
        {!slot.isBreak && (
          <input
            type="text"
            value={slot.message || ""}
            onChange={(e) => handleChange(day, i, "message", e.target.value)}
          />
        )}
      </td>
    </tr>
  ))}
</tbody>

          </table>

          <button className="save-btn" onClick={handleSave}>💾 Save Timetable</button>
        </div>
      </div>
    </>
  );
};

export default ClassTimetable;
